import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUnleashClient } from '@unleash/proxy-client-react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { CHECKOUT, ACTIVATION, GET_SURVEYS_INFO } from '../../services/ApolloClient';
import { useApp, useProducts } from '../../contexts';
import { CheckoutCartItem, CheckoutTypeEnum } from '../../types/products';
import { getLocalStorageItem, getProductsPurchaseItems } from '../../utils';
import { ErrorModal } from '../../components/ErrorModal';
import { getCheckoutErrorMessage } from './checkoutErrors';

function Checkout() {
  const [searchParams] = useSearchParams();
  const [checkout] = useLazyQuery(CHECKOUT);
  const [activation] = useMutation(ACTIVATION);
  const { setLoading } = useApp();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { cartItems } = useProducts();
  const referral = searchParams.get('referral_id');
  const flLevel = searchParams.get('flLevel');
  const medium = searchParams.get('medium');
  const source = searchParams.get('source');

  if (medium) searchParams.set('utm_medium', medium);
  if (source) searchParams.set('utm_source', source);

  const unleashClient = useUnleashClient();
  const enableFlexPaymentMethod = useMemo(() => {
    return unleashClient.isEnabled('enableFlexPaymentMethod');
  }, [unleashClient]);
  const { data: surveyInfoData, loading: surveyInfoDataLoading } = useQuery(GET_SURVEYS_INFO, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    setLoading(true);
  }, [setLoading]);

  const checkoutRedirect = useCallback(
    async (
      priceId: string | null,
      cart: Array<CheckoutCartItem> | null,
      referralId: string | null = null,
      coupon: string | null = null,
    ) => {
      const queryParams = Object.fromEntries(searchParams.entries());
      try {
        const checkoutRes = await checkout({
          variables: { priceId, cart, coupon, referralId, queryParams },
        });

        if (checkoutRes.error) {
          setLoading(false);
          setErrorMessage(getCheckoutErrorMessage(checkoutRes.error));
          return;
        }

        if (checkoutRes.data?.checkout?.checkoutType === CheckoutTypeEnum.ACTIVATION) {
          activation();
        }
        if (referralId) {
          window.location.href = JSON.parse(checkoutRes.data?.checkout?.hostedPage).url;
        } else if (enableFlexPaymentMethod && flLevel) {
          const checkoutUrl = JSON.parse(checkoutRes.data?.checkout?.hostedPage).url;
          const url = new URL(`${window.location.origin}/members/choose-payment-method`);

          url.searchParams.set('checkoutUrl', checkoutUrl);
          if (flLevel) url.searchParams.set('flLevel', flLevel);
          if (medium) url.searchParams.set('utm_medium', medium);
          if (source) url.searchParams.set('utm_source', source);
          window.location.href = url.toString();
        } else {
          window.location.href = JSON.parse(checkoutRes.data?.checkout?.hostedPage).url;
        }
      } catch (e: unknown) {
        setLoading(false);
        setErrorMessage(getCheckoutErrorMessage(e));
      }
    },
    [
      checkout,
      enableFlexPaymentMethod,
      activation,
      flLevel,
      medium,
      source,
      setLoading,
      searchParams,
    ],
  );

  useEffect(() => {
    if (surveyInfoDataLoading) {
      return;
    }

    if (surveyInfoData?.surveysInfo.showMetroPt) {
      window.location.href = `${
        window.location.origin
      }/members/metro-pt/?${searchParams.toString()}`;
      return;
    }

    const fromProduct = searchParams.get('fromProduct');
    if (fromProduct) {
      checkoutRedirect(null, getProductsPurchaseItems(cartItems));
      return;
    }

    if (referral) {
      checkoutRedirect(null, null, referral);
      return;
    }

    const level = searchParams.get('level');
    const coupon = getLocalStorageItem('coupon');
    if (level) {
      checkoutRedirect(level, null, null, coupon);
      return;
    }

    setLoading(false);
    window.location.href = `${window.location.origin}/app/your-path/`;
  }, [
    checkoutRedirect,
    searchParams,
    setLoading,
    cartItems,
    surveyInfoData,
    surveyInfoDataLoading,
    referral,
  ]);

  return (
    <ErrorModal
      isOpen={Boolean(errorMessage)}
      message={errorMessage ?? ''}
      onSubmit={() => {
        window.location.href = `${window.location.origin}/app/your-path/`;
      }}
    />
  );
}

export default Checkout;
